/**
 * PermissionDenied — centered "not authorized" card.
 */

import React from 'react'
import { ShieldAlert } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { logger } from '../utils/logger'
import { ERROR_MESSAGES } from '../constants/errorMessages'

export interface PermissionDeniedProps {
  /** The action that was denied (e.g., "update", "delete", "view") */
  action?: 'update' | 'delete' | 'view' | 'create'
  /** The resource type (e.g., "model", "prompt", "agent") */
  resource?: string
  /** Custom message to display */
  message?: string
  /** Whether to show as inline component (no centering, smaller padding) */
  inline?: boolean
  /** Custom CSS class */
  className?: string
  /** Callback when user clicks to request access */
  onRequestAccess?: () => void
}

const resolveMessage = (
  action: PermissionDeniedProps['action'],
  resource?: string,
  message?: string,
): string => {
  if (message) return message
  if (!resource) return "You don't have permission to view this page. Please contact your administrator to request access."
  switch (action) {
    case 'update':
      return ERROR_MESSAGES.PERMISSION_DENIED_UPDATE(resource)
    case 'delete':
      return ERROR_MESSAGES.PERMISSION_DENIED_DELETE(resource)
    case 'create':
      return ERROR_MESSAGES.PERMISSION_DENIED_CREATE(resource)
    case 'view':
    default:
      return ERROR_MESSAGES.PERMISSION_DENIED_VIEW(resource)
  }
}

export const PermissionDenied: React.FC<PermissionDeniedProps> = ({
  action = 'view',
  resource,
  message,
  inline = false,
  className = '',
  onRequestAccess,
}) => {
  const subtitle = resolveMessage(action, resource, message)
  const { logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
    } catch (err) {
      logger.error('Failed to logout', err instanceof Error ? err : undefined)
    }
  }

  const wrapperStyle: React.CSSProperties = inline
    ? { display: 'flex', justifyContent: 'center', padding: '1rem', width: '100%' }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        padding: '2rem',
        width: '100%',
      }

  return (
    <div style={wrapperStyle} className={className}>
      <div
        style={{
          textAlign: 'center',
          maxWidth: 380,
          width: '100%',
          padding: '2.5rem 2rem',
          borderRadius: 12,
          border: '1px solid var(--stroke, #e2e8f0)',
          background: 'var(--surface, #ffffff)',
          boxShadow:
            '0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px -12px rgba(0, 0, 0, 0.12)',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            color: 'var(--primary, #ff6600)',
            background: 'rgba(255, 102, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem',
          }}
        >
          <ShieldAlert size={26} strokeWidth={1.75} aria-hidden />
        </div>

        <h2
          style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--text, #111827)',
            margin: '0 0 0.5rem',
            lineHeight: 1.3,
          }}
        >
          Access denied
        </h2>

        <p
          style={{
            color: 'var(--text-muted, #6c757d)',
            fontSize: '0.875rem',
            margin: '0 0 1.75rem',
            lineHeight: 1.55,
          }}
        >
          {subtitle}
        </p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {onRequestAccess && (
            <button
              type="button"
              onClick={onRequestAccess}
              className="btn btn--outline"
              style={{
                width: '100%',
                height: 38,
                justifyContent: 'center',
                fontSize: '0.875rem',
              }}
            >
              Request Access
            </button>
          )}

          <button
            type="button"
            className="btn btn--primary"
            onClick={handleLogout}
            style={{
              width: '100%',
              height: 38,
              justifyContent: 'center',
              fontSize: '0.875rem',
            }}
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    </div>
  )
}

export default PermissionDenied
